import { firebaseApp } from "config/firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import { removeVideoServiceConfig } from "./RemoteVideoService.config";

interface ThumbnailAssetRequestType {
  playbackId: string;
  thumbnail: { time: number; height: number; width: number };
  tokenApplication: "Thumbnail";
}

interface VideoAssetRequestType {
  playbackId: string;
  tokenApplication: "Video";
}

type AssetRequestType = ThumbnailAssetRequestType | VideoAssetRequestType;

interface AssetResponseType {
  jwt: string;
}

export interface ThumbnailOptions {
  /** time (float) – Timestamp (in seconds) to extract the thumbnail. Defaults to the middle of the video. */
  time?: number;
  /** height (int32) – Thumbnail height in pixels. Defaults to the original video height. */
  height?: number;
  /** width (int32) – Thumbnail width in pixels. Defaults to the original video width. */
  width?: number;
  /** rotate (int32) – Rotates the image clockwise (90, 180, or 270 degrees). */
  rotate?: number;
  /**
   * fit_mode (string) – Defines how the image fits within width and height:
   *  preserve: Maintains aspect ratio while fitting within the dimensions.
   *  stretch: Forces the image to fill dimensions, possibly distorting it.
   *  crop: Scales and centers the image, cropping excess areas.
   *  smartcrop: Uses an algorithm to center the most relevant part.
   *  pad: Adds black padding to maintain aspect ratio while matching dimensions.
   */
  fit_mode?: "preserve" | "stretch" | "crop" | "smartcrop" | "pad";
  /** flip_v (boolean) – Flips the image vertically after transformations. */
  flip_v?: boolean;
  /** flip_h (boolean) – Flips the image horizontally after transformations. */
  flip_h?: boolean;
}

class RemoteVideoService {
  static async getAsset(args: { playbackId: string; type: "Video" | "Thumbnail" }): Promise<string> {
    if (args.type === "Thumbnail") {
      return RemoteVideoService.getThumbnailToken(args.playbackId);
    }
    return RemoteVideoService.getVideoToken(args.playbackId);
  }

  /**
   * @description Get the (signed) thumbnail URL for a given playbackId.
   * Has as default options: fit_mode: "smartcrop".
   * @see https://www.mux.com/docs/guides/get-images-from-a-video#using-signed-urls
   */
  static async getThumbnailUrl(playbackId: string, options?: ThumbnailOptions): Promise<string> {
    const token = await RemoteVideoService.getAsset({ playbackId, type: "Thumbnail" });

    const opts: ThumbnailOptions = {
      fit_mode: "smartcrop",
      ...options,
    };

    /** Construct the query params */
    const urlParams = new URLSearchParams();
    Object.entries({ ...opts, token }).forEach(([key, value]) => {
      urlParams.append(key, String(value));
    });

    const params = urlParams.toString();

    return `https://image.mux.com/${playbackId}/thumbnail.png?${params}`;
  }

  /**
   * @description Get the (signed) video URL for a given playbackId.
   */
  static async getVideoToken(playbackId: string): Promise<string> {
    const functions = getFunctions(firebaseApp, removeVideoServiceConfig.region);
    const getRemoteStorageToken = httpsCallable<AssetRequestType, AssetResponseType>(functions, "mux-jwt-signing");

    const { data } = await getRemoteStorageToken({
      playbackId,
      tokenApplication: "Video",
    });

    return data.jwt;
  }

  static async getThumbnailToken(
    playbackId: string,
    thumbnail?: { time: number; height: number; width: number }
  ): Promise<string> {
    const thumbOptions = thumbnail || { time: 0.1, height: 30, width: 30 };

    const functions = getFunctions(firebaseApp, removeVideoServiceConfig.region);
    const getRemoteStorageToken = httpsCallable<AssetRequestType, AssetResponseType>(functions, "mux-jwt-signing");

    const { data } = await getRemoteStorageToken({
      playbackId,
      tokenApplication: "Thumbnail",
      thumbnail: thumbOptions,
    });

    return data.jwt;
  }
}

export default RemoteVideoService;
